import { Bath, BedDouble, Landmark, MapPin, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { ShortlistActionButton } from "@/components/agent-shortlist/ShortlistActionButton";
import { useSiteBranding } from "@/context/SiteContext";
import { getInvestmentBadgePresentation } from "@/lib/investmentBadge";
import { trackLeadIntent } from "@/lib/leadIntent";
import { formatCategoryLabel, formatListingMonthly, formatListingPrice, formatListingPsf, formatNumber, isListingPriceOnRequest } from "@/lib/portalHelpers";

const formatRecentlyUpdated = (value) => {
  if (!value) return "Recently updated";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Recently updated";

  return `Recently updated ${parsed.toLocaleDateString("en-SG", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })}`;
};

export const ListingCard = ({ listing }) => {
  const { branding, buildBrandedPath, getWhatsAppHref, isAgentPath, leadFormPath } = useSiteBranding();
  const coverImage = listing.image_url || listing.gallery_urls?.[0] || "";
  const investmentBadge = getInvestmentBadgePresentation(listing);
  const listingPath = buildBrandedPath(`/listings/${listing.id}`);
  const listingWhatsAppHref = getWhatsAppHref(`Hello, I would like to enquire about ${listing.title}.`);
  const advisorFirstName = String((branding?.name || "Advisor").split(" ")[0] || "Advisor");

  const perspectiveTooltip = isAgentPath
    ? "Different advisors may present the same home with slightly different local insight. Open the profile you prefer and continue the conversation there."
    : "Each Founding Member offers a unique Signature Reply Playbook. Choose an agent to see their specific strategic perspective on this property.";

  return (
  <article className="agent-portal-card overflow-hidden rounded-[30px] border border-[#D4AF37]/45 bg-[#fffaf1] shadow-[0_22px_70px_rgba(0,25,53,0.08)]" data-testid={`listing-card-${listing.id}`}>
    <div className="relative aspect-[1.25/1] overflow-hidden">
      <Link data-testid={`listing-card-image-link-${listing.id}`} to={listingPath}>
        {coverImage ? (
          <img alt={listing.title} className="h-full w-full object-cover transition duration-500 hover:scale-105" data-testid={`listing-card-image-${listing.id}`} decoding="async" loading="lazy" src={coverImage} />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_top,rgba(212,175,55,0.2),transparent_35%),linear-gradient(180deg,#fdf7eb_0%,#f3ead7_100%)] text-sm font-semibold text-[#8b6d16]" data-testid={`listing-card-image-fallback-${listing.id}`}>
            Media loading from source
          </div>
        )}
      </Link>
      <span className="absolute left-4 top-4 rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/90 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#001935]" data-testid={`listing-status-badge-${listing.id}`}>
        {listing.category === "new_launch" ? "New Launch" : "Selling Fast"}
      </span>
    </div>

    <div className="space-y-5 p-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-[#8b6d16]" data-testid={`listing-category-${listing.id}`}>
          {formatCategoryLabel(listing.category)}
        </span>
        {listing.category === "new_launch" && isListingPriceOnRequest(listing) ? (
          <span className="rounded-full border border-[#D4AF37]/35 bg-[#fff4db] px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]" data-testid={`listing-price-on-request-badge-${listing.id}`}>
            Price available on request
          </span>
        ) : null}
        <span className="rounded-full border border-[#001935]/10 px-3 py-1 text-xs font-semibold text-[#42566f]" data-testid={`listing-district-${listing.id}`}>
          {listing.district}
        </span>
        {investmentBadge ? (
          <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] ${investmentBadge.className}`} data-testid={`listing-investment-badge-${listing.id}`}>
            {investmentBadge.emoji ? <span aria-hidden="true" className="hidden sm:inline">{investmentBadge.emoji}</span> : null}
            <span className="hidden sm:inline">{investmentBadge.text}</span>
            <span className="sm:hidden">{investmentBadge.compactLabel}</span>
          </span>
        ) : null}
      </div>

      <div>
        <Link data-testid={`listing-title-link-${listing.id}`} to={listingPath}>
          <h3 className="text-2xl font-semibold text-[#001935]" data-testid={`listing-title-${listing.id}`}>
            {listing.title}
          </h3>
        </Link>
        {listing.owner_name ? (
          <p className="mt-2 text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]" data-testid={`listing-owner-name-${listing.id}`}>
            Listed by {listing.owner_name}
          </p>
        ) : null}
        {listing.also_listed_by?.length ? (
          <div className="mt-3 rounded-2xl border border-[#D4AF37]/25 bg-white/80 px-3 py-3" data-testid={`listing-also-listed-by-${listing.id}`}>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#001935]" data-testid={`listing-market-dominance-${listing.id}`}>
              {isAgentPath
                ? `Also represented by ${listing.represented_by_founder_count || listing.also_listed_by.length + 1} advisors in our network.`
                : `Market Dominance: Represented by ${listing.represented_by_founder_count || listing.also_listed_by.length + 1} of our Core Team of Agents.`}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">{isAgentPath ? "Other advisors" : "Also listed by"}</p>
              <div className="group relative">
                <button
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-[#D4AF37]/35 bg-[#fff4db] text-[11px] font-bold text-[#8b6d16]"
                  data-testid={`listing-perspective-tooltip-trigger-${listing.id}`}
                  type="button"
                >
                  ?
                </button>
                <div
                  className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 hidden w-64 -translate-x-1/2 rounded-[18px] bg-[#001935] px-4 py-3 text-sm leading-7 text-white shadow-[0_18px_50px_rgba(0,25,53,0.28)] group-hover:block"
                  data-testid={`listing-perspective-tooltip-content-${listing.id}`}
                >
                  {perspectiveTooltip}
                </div>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {listing.also_listed_by.map((agent) => (
                <Link
                  className="rounded-full border border-[#001935]/10 bg-[#fffaf1] px-3 py-1 text-xs font-semibold text-[#42566f] transition hover:border-[#D4AF37] hover:text-[#8b6d16]"
                  data-testid={`listing-alternate-agent-link-${listing.id}-${agent.agent_id}`}
                  key={`${listing.id}-${agent.agent_id}`}
                  to={agent.subdomain_slug ? `/agent/${agent.subdomain_slug}/listings/${agent.listing_id}` : listingPath}
                >
                  {agent.agent_name}
                </Link>
              ))}
            </div>
          </div>
        ) : null}
        <p className="mt-2 flex items-center gap-2 text-sm text-[#42566f]" data-testid={`listing-address-${listing.id}`}>
          <MapPin className="h-4 w-4 text-[#D4AF37]" /> {listing.address}
        </p>
      </div>

      <div className="grid gap-3 text-sm text-[#42566f] sm:grid-cols-3">
        <div className="flex items-center gap-2" data-testid={`listing-bedrooms-${listing.id}`}>
          <BedDouble className="h-4 w-4 text-[#D4AF37]" /> {listing.bedrooms || "-"} Beds
        </div>
        <div className="flex items-center gap-2" data-testid={`listing-bathrooms-${listing.id}`}>
          <Bath className="h-4 w-4 text-[#D4AF37]" /> {listing.bathrooms} Baths
        </div>
        <div className="flex items-center gap-2" data-testid={`listing-property-type-${listing.id}`}>
          <Landmark className="h-4 w-4 text-[#D4AF37]" /> {listing.property_type}
        </div>
      </div>

      <div className="agent-paper-soft grid gap-3 rounded-2xl border border-[#001935]/10 bg-white/70 p-4 text-sm text-[#42566f] sm:grid-cols-3">
        <div data-testid={`listing-price-${listing.id}`}>
          <p className="text-xs uppercase tracking-[0.2em] text-[#7b8491]">Price</p>
          <p className="mt-1 font-bold text-[#001935]">{formatListingPrice(listing)}</p>
        </div>
        <div data-testid={`listing-psf-${listing.id}`}>
          <p className="text-xs uppercase tracking-[0.2em] text-[#7b8491]">PSF</p>
          <p className="mt-1 font-bold text-[#001935]">{formatListingPsf(listing)}</p>
        </div>
        <div data-testid={`listing-installment-${listing.id}`}>
          <p className="text-xs uppercase tracking-[0.2em] text-[#7b8491]">Est. Monthly</p>
          <p className="mt-1 font-bold text-[#001935]">{formatListingMonthly(listing)}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[#42566f]" data-testid={`listing-size-${listing.id}`}>
          {formatNumber(listing.sqft)} sqft • {listing.tenure}
        </p>
        <p className="rounded-full border border-[#001935]/10 bg-white/80 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#42566f]" data-testid={`listing-recently-updated-${listing.id}`}>
          {formatRecentlyUpdated(listing.updated_at)}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <ShortlistActionButton listing={listing} testId={`listing-shortlist-button-${listing.id}`} variant="outline" />
        <a
          className="gold-button !w-full"
          data-testid={`listing-contact-agent-button-${listing.id}`}
          href={listingWhatsAppHref}
          onClick={() => {
            void trackLeadIntent({
              eventType: "whatsapp_enquire_click",
              listing,
              metadata: { source: "listing_card_whatsapp" },
            });
          }}
          rel="noreferrer"
          target="_blank"
        >
          <MessageCircle className="mr-2 h-4 w-4" /> {isAgentPath ? `WhatsApp ${advisorFirstName}` : "WhatsApp"}
        </a>
        <Link className="outline-button !w-full" data-testid={`listing-lead-form-button-${listing.id}`} to={leadFormPath}>
          {isAgentPath ? "Request Details" : "Contact Us"}
        </Link>
      </div>

      {listing.source_note ? (
        <p className="border-t border-[#D4AF37]/25 pt-4 text-[11px] leading-6 text-[#6c7480]" data-testid={`listing-source-note-${listing.id}`}>
          {listing.source_note}
        </p>
      ) : null}
    </div>
  </article>
  );
};